import { fetcher } from '@commenda/utils';
import { BulkSendMessageResultItem } from '../chatRoom/bulkSendMessage';
import { BulkSendCompanyMessageResultItem } from '../chatRoom/bulkSendCompanyMessage';

export type TJobTask = {
  id: string;
  status: string;
  metadata: unknown;
  errorMessage: string | null;
};

export type TBulkMessageJobStatus = {
  isJobCompleted: boolean;
  job: {
    id: string;
    totalRows: number | null;
    processedRows: number | null;
    successCount: number | null;
    failureCount: number | null;
    startedAt: string | null;
    finishedAt: string | null;
  };
  totalTasks: number;
  completedTasks: number;
  status: 'RUNNING' | 'COMPLETED' | 'FAILED' | 'NOT_STARTED';
  tasks?: TJobTask[];
};

/**
 * Fetches the status of a bulk message job by job ID.
 * Used for polling during async bulk message processing.
 */
export async function getBulkMessageJobStatus(
  jobId: string
): Promise<TBulkMessageJobStatus | null> {
  try {
    const response = await fetcher(`job/${jobId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      return null;
    }

    return await response.json();
  } catch {
    return null;
  }
}

/**
 * Extracts per-SR failure results from completed job task metadata.
 */
export function extractFailedResultsFromTasks(
  tasks: TJobTask[]
): BulkSendMessageResultItem[] {
  const failedResults: BulkSendMessageResultItem[] = [];

  for (const task of tasks) {
    const metadata = task.metadata as {
      results?: BulkSendMessageResultItem[];
    } | null;

    if (!metadata?.results) continue;

    for (const result of metadata.results) {
      if (!result.success) {
        failedResults.push(result);
      }
    }
  }

  return failedResults;
}

/**
 * Extracts per-company failure results from completed job task metadata
 * for SEND_BULK_GENERAL_THREADS_MESSAGE jobs.
 */
export function extractFailedCompanyResultsFromTasks(
  tasks: TJobTask[]
): BulkSendCompanyMessageResultItem[] {
  const failedResults: BulkSendCompanyMessageResultItem[] = [];

  for (const task of tasks) {
    const metadata = task.metadata as {
      results?: BulkSendCompanyMessageResultItem[];
    } | null;

    if (!metadata?.results) continue;

    for (const result of metadata.results) {
      if (!result.success) {
        failedResults.push(result);
      }
    }
  }

  return failedResults;
}
